import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    IconButton,
    Tooltip,
    Chip,
    FormControl,
    Select,
    MenuItem,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    ContentCopy as CopyIcon,
    Favorite as FavoriteIcon,
    FavoriteBorder as FavoriteBorderIcon,
    SportsEsports as GamepadIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';
import { isUserSuperAdmin } from '../../services/tracker-limited-data';
import DeckStatsModal from './DeckStatsModal';
import { useNavigate } from 'react-router-dom';
import { getAllInkColors } from './InkColorIcon';
import DeckVisual from './DeckVisual';

const DeckCard = ({
    deck,
    currentUser,
    isDeckFavorite,
    getUserDeckCategory,
    getCategoryColor,
    getCategoryLabel,
    onToggleFavorite,
    onCopyDeckList,
    onEditDeck,
    onDeleteDeck,
    onCategoryChange,
    onImageClick,
    onOpenQuickMatch
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [statsModalOpen, setStatsModalOpen] = useState(false);
    const inkColors = getAllInkColors();

    // Sprawdź czy użytkownik jest superadministratorem
    useEffect(() => {
        const checkSuperAdminStatus = async () => {
            if (currentUser) {
                try {
                    const superAdminStatus = await isUserSuperAdmin(currentUser.uid);
                    setIsSuperAdmin(superAdminStatus);
                } catch (error) {
                    console.error('Błąd podczas sprawdzania uprawnień superadministratora:', error);
                    setIsSuperAdmin(false);
                }
            } else {
                setIsSuperAdmin(false);
            }
        };

        checkSuperAdminStatus();
    }, [currentUser]);

    // Sprawdź czy użytkownik może edytować/usunąć talię
    const canEditDeck = currentUser && (deck.author === currentUser.uid || isSuperAdmin);

    const handleStatsClick = (e) => {
        e.stopPropagation();
        navigate(`/tracker/${deck.id}`);
    };

    const handleDeckNameClick = (e) => {
        e.stopPropagation();
        navigate(`/tracker/${deck.id}`);
    };

    const getInkColor = (color) => {
        const colorData = inkColors.find(c => c.value === color);
        return colorData?.color || '#666';
    };

    // Funkcja pomocnicza do wyświetlania statystyk talii
    const renderDeckStats = (deckStats) => {
        if (!deckStats || !deckStats.overall) return null;

        const { overall } = deckStats;
        const winrateColor = overall.winrate >= 60 ? '#10b981' : overall.winrate >= 50 ? '#f59e0b' : '#ef4444';

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    mt: 1,
                    p: 1,
                    backgroundColor: 'rgba(17, 24, 39, 0.3)',
                    borderRadius: '8px',
                    border: '1px solid rgba(76, 29, 149, 0.2)',
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: 'rgba(17, 24, 39, 0.5)',
                        border: '1px solid rgba(76, 29, 149, 0.4)'
                    }
                }}
                onClick={handleStatsClick}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AssessmentIcon sx={{ color: '#7c3aed', fontSize: 16 }} />
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, fontSize: '0.65rem' }}>
                        Statystyki (kliknij)
                    </Typography>
                </Box>

                {/* Ikony kolorów atramentu talii */}
                {deck.archetype?.ink_colors && deck.archetype.ink_colors.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.3, mb: 0.5 }}>
                        {deck.archetype.ink_colors.map((color, index) => (
                            <Box
                                key={index}
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: getInkColor(color),
                                    border: `1px solid ${getInkColor(color)}`
                                }}
                            />
                        ))}
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                        label={`${overall.winrate}%`}
                        size="small"
                        sx={{
                            backgroundColor: `${winrateColor}20`,
                            color: winrateColor,
                            border: `1px solid ${winrateColor}40`,
                            fontSize: '0.6rem',
                            height: 16,
                            fontWeight: 600
                        }}
                    />
                    <Chip
                        label={`${overall.wins}W`}
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(16, 185, 129, 0.2)',
                            color: '#10b981',
                            border: '1px solid rgba(16, 185, 129, 0.4)',
                            fontSize: '0.6rem',
                            height: 16
                        }}
                    />
                    <Chip
                        label={`${overall.losses}L`}
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(239, 68, 68, 0.2)',
                            color: '#ef4444',
                            border: '1px solid rgba(239, 68, 68, 0.4)',
                            fontSize: '0.6rem',
                            height: 16
                        }}
                    />
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)', fontSize: '0.6rem' }}>
                    {overall.games} gier łącznie
                </Typography>
            </Box>
        );
    };

    return (
        <>
            <Card sx={{
                backgroundColor: 'rgba(17, 24, 39, 0.4)',
                border: '1px solid rgba(76, 29, 149, 0.2)',
                borderRadius: '12px',
                transition: 'all 0.2s ease',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                    backgroundColor: 'rgba(17, 24, 39, 0.6)',
                    border: '1px solid rgba(76, 29, 149, 0.4)',
                    transform: isMobile ? 'none' : 'translateY(-2px)',
                    boxShadow: isMobile ? 'none' : '0 8px 25px rgba(76, 29, 149, 0.2)'
                }
            }}>
                {/* Zdjęcie talii */}
                <Box sx={{
                    position: 'relative',
                    height: isSmallMobile ? 150 : isMobile ? 180 : 200,
                    overflow: 'hidden',
                    borderTopLeftRadius: '12px',
                    borderTopRightRadius: '12px'
                }}>
                    <DeckVisual deck={deck} mode="card" onClick={() => onImageClick(deck)} />

                    {/* Wskaźnik właściciela */}
                    {deck.author !== currentUser?.uid && !isSuperAdmin && (
                        <Chip
                            label="Publiczna"
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: isSmallMobile ? 6 : 8,
                                right: isSmallMobile ? 6 : 8,
                                backgroundColor: 'rgba(16, 185, 129, 0.9)',
                                color: 'white',
                                fontSize: isSmallMobile ? '0.55rem' : '0.6rem',
                                height: isSmallMobile ? 18 : 20
                            }}
                        />
                    )}

                    {/* Wskaźnik superadministratora */}
                    {isSuperAdmin && deck.author !== currentUser?.uid && (
                        <Chip
                            label="SuperAdmin"
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: isSmallMobile ? 6 : 8,
                                right: isSmallMobile ? 6 : 8,
                                backgroundColor: 'rgba(239, 68, 68, 0.9)',
                                color: 'white',
                                fontSize: isSmallMobile ? '0.55rem' : '0.6rem',
                                height: isSmallMobile ? 18 : 20
                            }}
                        />
                    )}

                    {/* Przycisk ulubionych */}
                    {currentUser && (
                        <IconButton
                            size="small"
                            onClick={() => onToggleFavorite(deck.id)}
                            sx={{
                                position: 'absolute',
                                top: isSmallMobile ? 6 : 8,
                                left: isSmallMobile ? 6 : 8,
                                color: isDeckFavorite(deck.id) ? '#f59e0b' : 'rgba(255, 255, 255, 0.7)',
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                backdropFilter: 'blur(4px)',
                                width: isSmallMobile ? 28 : 32,
                                height: isSmallMobile ? 28 : 32,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    color: isDeckFavorite(deck.id) ? '#f59e0b' : '#f59e0b'
                                }
                            }}
                        >
                            {isDeckFavorite(deck.id) ? (
                                <FavoriteIcon sx={{ fontSize: isSmallMobile ? 18 : 22 }} />
                            ) : (
                                <FavoriteBorderIcon sx={{ fontSize: isSmallMobile ? 18 : 22 }} />
                            )}
                        </IconButton>
                    )}
                </Box>

                {/* Zawartość kafelka */}
                <CardContent sx={{
                    flexGrow: 1,
                    p: isSmallMobile ? 1.5 : 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                }}>
                    {/* Nazwa talii */}
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'white',
                            fontWeight: 600,
                            fontSize: isSmallMobile ? '0.9rem' : '1rem',
                            lineHeight: 1.2,
                            mb: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            '&:hover': {
                                color: '#c4b5fd'
                            }
                        }}
                        onClick={handleDeckNameClick}
                    >
                        {deck.name}
                    </Typography>
                    {/* Status użytkownika */}
                    {currentUser ? (
                        <FormControl size="small" sx={{ minWidth: '100%' }}>
                            <Select
                                value={getUserDeckCategory(deck.id)}
                                onChange={(e) => onCategoryChange(deck.id, e.target.value)}
                                sx={{
                                    color: getCategoryColor(getUserDeckCategory(deck.id)),
                                    fontSize: isSmallMobile ? '0.65rem' : '0.7rem',
                                    height: isSmallMobile ? 18 : 20,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none'
                                    },
                                    '& .MuiSelect-select': {
                                        padding: isSmallMobile ? '1px 6px' : '2px 8px',
                                        backgroundColor: `${getCategoryColor(getUserDeckCategory(deck.id))}20`,
                                        borderRadius: '8px',
                                        border: `1px solid ${getCategoryColor(getUserDeckCategory(deck.id))}40`
                                    },
                                    '&:hover .MuiSelect-select': {
                                        backgroundColor: `${getCategoryColor(getUserDeckCategory(deck.id))}30`
                                    }
                                }}
                            >
                                <MenuItem value="unknown">Nieznana</MenuItem>
                                <MenuItem value="to_test">Do przetestowania</MenuItem>
                                <MenuItem value="rejected">Odrzucona</MenuItem>
                                <MenuItem value="selected">Wybrana</MenuItem>
                            </Select>
                        </FormControl>
                    ) : (
                        <Chip
                            label={getCategoryLabel('unknown')}
                            size="small"
                            sx={{
                                backgroundColor: `${getCategoryColor('unknown')}20`,
                                color: getCategoryColor('unknown'),
                                border: `1px solid ${getCategoryColor('unknown')}40`,
                                fontSize: isSmallMobile ? '0.65rem' : '0.7rem',
                                height: isSmallMobile ? 18 : 20,
                                width: '100%'
                            }}
                        />
                    )}

                    {/* Statystyki talii */}
                    {renderDeckStats(deck.stats)}

                    {/* Przyciski akcji */}
                    <Box sx={{
                        display: 'flex',
                        gap: isSmallMobile ? 0.5 : 1,
                        mt: 'auto',
                        pt: 1,
                        justifyContent: isSmallMobile ? 'center' : 'flex-start'
                    }}>
                        {/* Przycisk meczu */}
                        {onOpenQuickMatch && (
                            <Tooltip title="Dodaj mecz">
                                <IconButton
                                    size="small"
                                    onClick={() => onOpenQuickMatch(deck.id)}
                                    sx={{
                                        color: 'rgba(34, 197, 94, 0.7)',
                                        width: isSmallMobile ? 28 : 32,
                                        height: isSmallMobile ? 28 : 32,
                                        '&:hover': {
                                            color: '#22c55e',
                                            backgroundColor: 'rgba(34, 197, 94, 0.1)'
                                        }
                                    }}
                                >
                                    <GamepadIcon sx={{ fontSize: isSmallMobile ? 18 : 22 }} />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Przycisk kopiowania */}
                        <Tooltip title="Kopiuj listę kart">
                            <IconButton
                                size="small"
                                onClick={() => onCopyDeckList(deck)}
                                sx={{
                                    color: 'rgba(196, 181, 253, 0.7)',
                                    width: isSmallMobile ? 28 : 32,
                                    height: isSmallMobile ? 28 : 32,
                                    '&:hover': {
                                        color: '#c4b5fd',
                                        backgroundColor: 'rgba(124, 58, 237, 0.1)'
                                    }
                                }}
                            >
                                <CopyIcon sx={{ fontSize: isSmallMobile ? 18 : 22 }} />
                            </IconButton>
                        </Tooltip>

                        {/* Przyciski edycji i usuwania - dla własnych talii lub administratorów */}
                        {canEditDeck && (
                            <>
                                <Tooltip title="Edytuj talię">
                                    <IconButton
                                        size="small"
                                        onClick={() => onEditDeck(deck)}
                                        sx={{
                                            color: 'rgba(196, 181, 253, 0.7)',
                                            width: isSmallMobile ? 28 : 32,
                                            height: isSmallMobile ? 28 : 32,
                                            '&:hover': {
                                                color: '#c4b5fd',
                                                backgroundColor: 'rgba(124, 58, 237, 0.1)'
                                            }
                                        }}
                                    >
                                        <EditIcon sx={{ fontSize: isSmallMobile ? 18 : 22 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Usuń talię">
                                    <IconButton
                                        size="small"
                                        onClick={() => onDeleteDeck(deck.id)}
                                        sx={{
                                            color: 'rgba(239, 68, 68, 0.7)',
                                            width: isSmallMobile ? 28 : 32,
                                            height: isSmallMobile ? 28 : 32,
                                            '&:hover': {
                                                color: '#ef4444',
                                                backgroundColor: 'rgba(239, 68, 68, 0.1)'
                                            }
                                        }}
                                    >
                                        <DeleteIcon sx={{ fontSize: isSmallMobile ? 18 : 22 }} />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Modal ze statystykami talii */}
            <DeckStatsModal
                open={statsModalOpen}
                onClose={() => setStatsModalOpen(false)}
                deck={deck}
            />
        </>
    );
};

export default DeckCard;
